import VueWrapper from '@/components/core/Vue/vue.wrapper';
import {CAP_DIRECTION, KeyValuePair, PIN_DIRECTION} from '@/globals';
import {
    CableModel,
    CreateEditCableViewModel,
    CreateEditEquipmentCapViewModel,
    CreateEditEquipmentPinViewModel,
    EquipmentCapModel,
    EquipmentPinModel,
} from '@/sdk';
import {CablesApi, EquipmentCapsApi, EquipmentConnectorPinsApi, EquipmentPinsApi} from '@/sdk/api-services';
import CapsDetailsFormComponent from '@/views/shared/detail-forms/caps-details-form/caps-details-form.component';
import PinDetailsFormComponent from '@/views/shared/detail-forms/pin-details-form/pin-details-form.component';
import {SiteLocationService} from '@/views/shared/services/site-location.service';
import * as go from 'gojs';
import Component from 'vue-class-component';
import {Prop} from 'vue-property-decorator';
import {createCapsNodeTemplate, createPinsNodeTemplate} from './equipment-circuit-diagram-utils';

@Component({
    components: {
        CapsDetailsFormComponent,
        PinDetailsFormComponent,
    },
})
export default class EquipmentCircuitDiagramComponent extends VueWrapper {
    public $refs!: {
        tableContainer: Vue;
    };
    @Prop({
        type: String,
        required: true,
    })
    public readonly locationId!: string;

    @Prop({
        type: Boolean,
        required: true,
        default: false,
    })
    public readonly readonly!: boolean;

    public pinsInToAddCount: number = 0;
    public pinsOutToAddCount: number = 0;

    public capsInToAddCount: number = 0;
    public capsOutToAddCount: number = 0;

    readonly name = 'add-edit-pin-dialog';

    public equipmentPinsApi: EquipmentPinsApi = new EquipmentPinsApi();
    public equipmentCapsApi: EquipmentCapsApi = new EquipmentCapsApi();
    public equipmentConnectorPinsApi: EquipmentConnectorPinsApi = new EquipmentConnectorPinsApi();
    public cablesApi: CablesApi = new CablesApi();

    public siteLocationsSrv: SiteLocationService = new SiteLocationService();

    public inPinsList: Array<EquipmentPinModel> = [];
    public outPinsList: Array<EquipmentPinModel> = [];
    // public inConnectorPinsList: Array<EquipmentConnectorPinModel> = [];
    // public outConnectorPinsList: Array<EquipmentConnectorPinModel> = [];

    public inCapsList: Array<EquipmentCapModel> = [];
    public outCapsList: Array<EquipmentCapModel> = [];

    public inCablesList: Array<CableModel> = [];
    public outCablesList: Array<CableModel> = [];

    public selectedCap: EquipmentCapModel | null = null;
    public selectedPin: EquipmentPinModel | null = null;

    public pinDirections: Array<KeyValuePair> = [
        {
            key: PIN_DIRECTION.IN,
            value: 'In',
        },
        {
            key: PIN_DIRECTION.OUT,
            value: 'Out',
        },
    ];

    public get hash() {
        const ids = [
            ...this.inPinsList.map(x => x.id),
            ...this.outPinsList.map(x => x.id),
            ...this.inCapsList.map(x => x.id),
            ...this.outCapsList.map(x => x.id),
        ];

        ids.sort(() => Math.random() - 0.5);

        return ids.join('');
    }

    public get hasCircuitEditPermission(): boolean {
        return this.$helpers.hasPermission(this.$helpers.PERMISSION_KEYS.CIRCUIT_EDIT, this.siteLocationsSrv.site?.id ?? null);
    }

    public onScroll(event: Event) {}

    private myDiagram: go.Diagram | null = null;

    public mounted(): void {
        setTimeout(() => {
            this.initializeGoJsDiagram();
        }, 1000);

        const promises = [
            this.getEquipmentPins(),
            this.getEquipmentCaps(),
            // this.getEquipmentConnectorPins()
        ];

        // new MutationObserver(() => {
        //     console.log('scroll');
        // }).observe(this.$refs.tableContainer.$el, {
        //     attributes: true,
        //     childList: true,
        //     subtree: true,
        // });
    }

    private initializeGoJsDiagram(): void {
        const $ = go.GraphObject.make;

        this.myDiagram = $(go.Diagram, 'gojsPanel', {
            'draggingTool.isEnabled': this.hasCircuitEditPermission && !this.readonly, // Enable dragging only with permission
            'undoManager.isEnabled': this.hasCircuitEditPermission && !this.readonly, // Enable undo/redo only with permission
            'linkingTool.isEnabled': this.hasCircuitEditPermission && !this.readonly, // Enable link drawing only with permission
            'relinkingTool.isEnabled': this.hasCircuitEditPermission && !this.readonly, // Enable link editing only with permission
            'grid.visible': true,
            allowHorizontalScroll: true,
            allowVerticalScroll: true,
            // layout: $(go.GridLayout, {
            //     wrappingColumn: 3, // Two columns for Wire In and Wire Out
            //     alignment: go.GridLayout.Position,
            //     cellSize: new go.Size(1, 1),
            //     spacing: new go.Size(30, 10),
            // }),
        });

        // const leftConnectorPinsNodeTemplate = createConnectorPinsNodeTemplate(
        //     {
        //         direction: 'left',
        //         readonly: this.readonly,
        //     },
        //     {
        //         onDeleteConnectorPinClick: (pinId: string) => {
        //             const cPin = [...this.inConnectorPinsList, ...this.outConnectorPinsList].find(x => x.id === pinId);
        //             if (cPin) {
        //                 this.onDeleteConnectorPinClick(cPin);
        //             }
        //         },
        //     }
        // );

        // const rightConnectorPinsNodeTemplate = createConnectorPinsNodeTemplate(
        //     {
        //         direction: 'right',
        //         readonly: this.readonly,
        //     },
        //     {
        //         onDeleteConnectorPinClick: (pinId: string) => {
        //             const cPin = [...this.inConnectorPinsList, ...this.outConnectorPinsList].find(x => x.id === pinId);
        //             if (cPin) {
        //                 this.onDeleteConnectorPinClick(cPin);
        //             }
        //         },
        //     }
        // );

        const afterPinAddButtonTemplate = $(
            go.Node,
            go.Panel.Horizontal,
            {
                margin: 0,
                selectable: false,
                movable: false,
            },
            $(
                go.Panel,
                go.Panel.Auto,
                {
                    margin: 0,
                },
                $(go.Shape, 'RoundedRectangle', {
                    fill: '#f5f5f5',
                    stroke: '#cccccc',
                    strokeWidth: 2,
                    parameter1: 15,
                    height: 20,
                    width: 20,
                }),
                $(
                    go.Panel,
                    go.Panel.Horizontal,
                    {
                        margin: 0,
                        width: NaN,
                    },
                    $(go.Picture, {
                        source: '/icons/gridicons--add.svg',
                        width: 18,
                        height: 18,
                        cursor: 'pointer',
                        margin: new go.Margin(0, 0, 0, 0),
                        imageAlignment: go.Spot.Center,
                        imageStretch: go.GraphObject.Uniform,
                        scale: 1,
                        click: (e: any, obj: any) => {
                            // Check permissions before allowing add actions
                            if (!this.hasCircuitEditPermission || this.readonly) return;

                            const node = obj.part;
                            if (node) {
                                const data = node.data;
                                if (data) {
                                    // if (data.group === 'IN_CAPS') {
                                    //     const capId = data.capId;
                                    //     const cap = this.inCapsList.find(x => x.id === capId);
                                    //     if (cap) {
                                    //         this.addSingleCap(cap, data.where);
                                    //     } else {
                                    //         this.addSingleCap({sortOrder: 1, capDirection: CAP_DIRECTION.IN} as EquipmentCapModel, data.where);
                                    //     }
                                    // } else if (data.group === 'OUT_CAPS') {
                                    //     const capId = data.capId;
                                    //     const cap = this.outCapsList.find(x => x.id === capId);
                                    //     if (cap) {
                                    //         this.addSingleCap(cap, data.where);
                                    //     } else {
                                    //         this.addSingleCap({sortOrder: 1, capDirection: CAP_DIRECTION.OUT} as EquipmentCapModel, data.where);
                                    //     }
                                    // }
                                    if (data.type === 'afterCap') {
                                        const capId = data.capId;
                                        const cap = this.inCapsList.find(x => x.id === capId);
                                        if (cap) {
                                            this.addSingleCap(cap, data.where);
                                        } else if (data.direction === CAP_DIRECTION.IN || data.direction === CAP_DIRECTION.OUT) {
                                            this.addSingleCap({sortOrder: 1, capDirection: data.direction} as EquipmentCapModel, data.where);
                                        }
                                    }
                                    // else if (data.group === 'IN_PINS') {
                                    //     const pinId = data.pinId;
                                    //     const pin = this.inPinsList.find(x => x.id === pinId);
                                    //     if (pin) {
                                    //         this.addSinglePin(pin, data.where);
                                    //     } else {
                                    //         this.addSinglePin({sortOrder: 1, pinDirection: PIN_DIRECTION.IN} as EquipmentPinModel, data.where);
                                    //     }
                                    // } else if (data.group === 'OUT_PINS') {
                                    //     const pinId = data.pinId;
                                    //     const pin = this.outPinsList.find(x => x.id === pinId);
                                    //     if (pin) {
                                    //         this.addSinglePin(pin, data.where);
                                    //     } else {
                                    //         this.addSinglePin({sortOrder: 1, pinDirection: PIN_DIRECTION.OUT} as EquipmentPinModel, data.where);
                                    //     }
                                    // }
                                    if (data.type === 'afterPin') {
                                        const pinId = data.pinId;
                                        const pin = this.inPinsList.find(x => x.id === pinId);
                                        if (pin) {
                                            this.addSinglePin(pin, data.where);
                                        } else if (data.direction === PIN_DIRECTION.IN || data.direction === PIN_DIRECTION.OUT) {
                                            this.addSinglePin({sortOrder: 1, pinDirection: data.direction} as EquipmentPinModel, data.where);
                                        }
                                    }
                                    // else if (data.group === 'CONNECTOR_PINS_IN') {
                                    //     const pinId = data.pinId;
                                    //     const pin = this.inConnectorPinsList.find(x => x.id === pinId);
                                    //     if (pin) {
                                    //         this.addSingleConnectorPin(pin, data.where);
                                    //     } else {
                                    //         this.addSingleConnectorPin(
                                    //             {sortOrder: 1, pinDirection: PIN_DIRECTION.IN} as EquipmentConnectorPinModel,
                                    //             data.where
                                    //         );
                                    //     }
                                    // } else if (data.group === 'CONNECTOR_PINS_OUT') {
                                    //     const pinId = data.pinId;
                                    //     const pin = this.outConnectorPinsList.find(x => x.id === pinId);
                                    //     if (pin) {
                                    //         this.addSingleConnectorPin(pin, data.where);
                                    //     } else {
                                    //         this.addSingleConnectorPin(
                                    //             {sortOrder: 1, pinDirection: PIN_DIRECTION.OUT} as EquipmentConnectorPinModel,
                                    //             data.where
                                    //         );
                                    //     }
                                    // }
                                }
                            }
                            console.log('Add button clicked', node.data);
                        },
                    })
                )
            )
        );

        this.myDiagram.linkTemplate = new go.Link({curve: go.Link.Bezier, layerName: 'Foreground'}).add(new go.Shape({strokeWidth: 3}));

        this.myDiagram.groupTemplateMap.add(
            'cableNodeGroupTemplate',
            $(
                go.Group,
                go.Panel.Vertical,
                {
                    selectable: true,
                    movable: true,
                    layout: $(go.GridLayout, {
                        wrappingColumn: 1,
                        alignment: go.GridLayout.Position,
                        cellSize: new go.Size(1, 1),
                        spacing: new go.Size(0, 2),
                        comparer: (a: go.Part, b: go.Part) => {
                            const aOrder = a.data.sortOrder ?? 0;
                            const bOrder = b.data.sortOrder ?? 0;
                            return aOrder - bOrder;
                        },
                    }),
                    isSubGraphExpanded: true,
                    computesBoundsAfterDrag: true,
                    handlesDragDropForMembers: true,
                    mouseDragEnter: (e, group: any) => {
                        group.isHighlighted = true;
                    },
                    mouseDragLeave: (e, group: any) => {
                        group.isHighlighted = false;
                    },
                    mouseDrop: (e, group: any) => {
                        const tool = e.diagram.toolManager.draggingTool;
                        const draggedParts = tool.draggedParts;
                        if (draggedParts) {
                            draggedParts.each(part => {
                                if (part instanceof go.Node) {
                                    part.containingGroup = group as any;
                                }
                            });
                        }
                        group.isHighlighted = false;
                        group.layout.invalidateLayout();

                        // Sort nodeDataArray data of the group based on sortOrder
                        const groupKey = group.data.key;
                        const groupNodes = me.myDiagram!.model.nodeDataArray.filter((node: any) => node.group === groupKey);

                        groupNodes.sort((a: any, b: any) => {
                            const nodeA = me.myDiagram!.findNodeForData(a);
                            const nodeB = me.myDiagram!.findNodeForData(b);
                            return (nodeA?.location.y ?? 0) - (nodeB?.location.y ?? 0);
                        });

                        groupNodes.forEach((node: any, index: number) => {
                            e.diagram.model.setDataProperty(node, 'sortOrder', index);
                        });
                    },
                },
                // Cable header with title and controls
                $(
                    go.Panel,
                    go.Panel.Auto,
                    {
                        margin: new go.Margin(2, 0, 2, 0),
                    },
                    $(go.Shape, 'Rectangle', {
                        fill: '#f5f5f5',
                        stroke: '#cccccc',
                        strokeWidth: 1,
                        parameter1: 15,
                        width: 348,
                    }),
                    $(
                        go.Panel,
                        go.Panel.Vertical,
                        {},
                        $(
                            go.Panel,
                            go.Panel.Horizontal,
                            {
                                margin: 0,
                            },
                            $(
                                go.Panel,
                                go.Panel.Auto,
                                {
                                    margin: new go.Margin(0, 7, 0, 1),
                                },
                                // Add border to each Picture by wrapping in a Panel with a Shape as border
                                $(go.Shape, 'Rectangle', {
                                    desiredSize: new go.Size(24, 24),
                                    stroke: '#1976d2',
                                    strokeWidth: 1,
                                    fill: 'transparent',
                                    // geometryString: idx === 0 ? 'M0,0 L0,24 L24,24 L24,0 Z' : 'M0,0 L0,110 L200,110 L200,0',
                                }),
                                $(go.Picture, {
                                    source: '/icons/link-variant.svg',
                                    width: 20,
                                    height: 20,
                                    cursor: 'pointer',
                                    margin: new go.Margin(0, 0, 0, 0),
                                    imageAlignment: go.Spot.Center,
                                    imageStretch: go.GraphObject.Uniform,
                                    scale: 1,
                                    fromLinkable: false,
                                    toLinkable: this.hasCircuitEditPermission && !this.readonly,
                                    toLinkableDuplicates: false,
                                    toMaxLinks: 1,
                                    portId: `cable-left-side`,
                                })
                            ),
                            $(
                                go.Panel,
                                go.Panel.Vertical,
                                {
                                    margin: 0,
                                    width: 200,
                                },
                                $(
                                    go.Panel,
                                    go.Panel.Horizontal,
                                    {
                                        margin: 0,
                                        width: NaN,
                                    },
                                    $(
                                        go.TextBlock,
                                        {
                                            margin: new go.Margin(0, 10, 0, 0),
                                            font: 'bold 12pt sans-serif',
                                            cursor: 'pointer',
                                            alignment: go.Spot.Center,
                                            click: (e: any, obj: any) => {
                                                // Prevent click event from propagating to the node
                                                // e.stopPropagation();
                                                const node = obj.part;
                                                const cableId = node?.data?.key;
                                                // if (cableId) {
                                                //     me.onCableClick(cableId);
                                                // } else {
                                                //     me.selectedCable = null;
                                                // }
                                            },
                                        },
                                        new go.Binding('text', 'text')
                                    ),
                                    $(
                                        go.Shape,
                                        'Circle',
                                        {
                                            desiredSize: new go.Size(14, 14),
                                            stroke: 'black',
                                            fill: 'white',
                                            strokeWidth: 2,
                                            cursor: 'pointer',
                                            margin: new go.Margin(0, 0, 0, 0),
                                        },
                                        new go.Binding('fill', 'isActive', function (s) {
                                            return s ? 'black' : 'white';
                                        })
                                    )
                                )
                            ),
                            $(
                                go.Panel,
                                go.Panel.Auto,
                                {
                                    margin: new go.Margin(0, 7, 0, 1),
                                },
                                // Add border to each Picture by wrapping in a Panel with a Shape as border
                                $(go.Shape, 'Rectangle', {
                                    desiredSize: new go.Size(24, 24),
                                    stroke: '#1976d2',
                                    strokeWidth: 1,
                                    fill: 'transparent',
                                    // geometryString: idx === 0 ? 'M0,0 L0,24 L24,24 L24,0 Z' : 'M0,0 L0,110 L200,110 L200,0',
                                }),
                                $(go.Picture, {
                                    source: '/icons/link-variant.svg',
                                    width: 20,
                                    height: 20,
                                    cursor: 'pointer',
                                    margin: new go.Margin(0, 0, 0, 0),
                                    imageAlignment: go.Spot.Center,
                                    imageStretch: go.GraphObject.Uniform,
                                    scale: 1,
                                    fromLinkable: this.hasCircuitEditPermission && !this.readonly,
                                    toLinkable: false,
                                    toLinkableDuplicates: false,
                                    // fromMaxLinks: 1,
                                    portId: `cable-right-side`,
                                })
                            )
                            // $(go.Picture, {
                            //     source: '/icons/delete-empty-red.svg',
                            //     width: 20,
                            //     height: 20,
                            //     cursor: 'pointer',
                            //     margin: new go.Margin(0, 0, 0, 10),
                            //     imageAlignment: go.Spot.Center,
                            //     imageStretch: go.GraphObject.Uniform,
                            //     scale: 1,
                            //     click: (e: any, obj: any) => {
                            //         const group = obj.part;
                            //         const cableId = group?.data?.key;
                            //         // if (cableId) {
                            //         //     const cable = me.cables.find(c => c.id === cableId);
                            //         //     if (cable) {
                            //         //         me.onDeleteCable(cable);
                            //         //     }
                            //         // }
                            //     },
                            // })
                            // $(
                            //     go.Panel,
                            //     go.Panel.Auto,
                            //     {
                            //         margin: new go.Margin(0, 0, 0, 7),
                            //         cursor: 'pointer',
                            //         click: (e: any, obj: any) => {
                            //             const group = obj.part;
                            //             const cableId = group?.data?.key;
                            //             if (cableId && !me.readonly) {
                            //                 // me.openSelectCapDialog(cableId, 'in');
                            //             }
                            //         },
                            //     },
                            //     $(go.Shape, 'Rectangle', {
                            //         fill: '#f5f5f5',
                            //         stroke: '#000',
                            //         strokeWidth: 1,
                            //         parameter1: 15,
                            //     }),
                            //     $(
                            //         go.TextBlock,
                            //         {
                            //             margin: new go.Margin(0, 0, 0, 0),
                            //             stroke: '#1976d2',
                            //         },
                            //         new go.Binding('text', 'key', cableId => {
                            //             return 'Select In Cap';
                            //         })
                            //     )
                            // )
                        ),
                        // Placeholder for child nodes (cores)
                        $(
                            go.Panel,
                            go.Panel.Auto,
                            {
                                margin: 0,
                            },
                            new go.Placeholder({padding: 1}) // Add padding inside the rectangle
                        )
                        // Display selected caps
                        // $(
                        //     go.Panel,
                        //     go.Panel.Horizontal,
                        //     {
                        //         margin: new go.Margin(5, 0, 0, 0),
                        //     },
                        //     $(
                        //         go.Panel,
                        //         go.Panel.Auto,
                        //         {
                        //             margin: new go.Margin(0, 5, 0, 0),
                        //         },
                        //         $(go.Shape, 'RoundedRectangle', {
                        //             fill: '#e8f5e8',
                        //             stroke: '#4caf50',
                        //             strokeWidth: 1,
                        //             parameter1: 5,
                        //         }),
                        //         $(
                        //             go.TextBlock,
                        //             {
                        //                 margin: new go.Margin(2, 5, 2, 5),
                        //                 font: '10pt sans-serif',
                        //                 stroke: '#2e7d32',
                        //             },
                        //             new go.Binding('text', 'selectedOutCapText', selectedOutCap => {
                        //                 return selectedOutCap !== 'Select Out Cap' ? `Out: ${selectedOutCap}` : '';
                        //             }),
                        //             new go.Binding('visible', 'selectedOutCapText', selectedOutCap => {
                        //                 return selectedOutCap !== 'Select Out Cap';
                        //             })
                        //         )
                        //     ),
                        //     $(
                        //         go.Panel,
                        //         go.Panel.Auto,
                        //         {
                        //             margin: new go.Margin(0, 0, 0, 5),
                        //         },
                        //         $(go.Shape, 'RoundedRectangle', {
                        //             fill: '#e3f2fd',
                        //             stroke: '#2196f3',
                        //             strokeWidth: 1,
                        //             parameter1: 5,
                        //         }),
                        //         $(
                        //             go.TextBlock,
                        //             {
                        //                 margin: new go.Margin(2, 5, 2, 5),
                        //                 font: '10pt sans-serif',
                        //                 stroke: '#1565c0',
                        //             },
                        //             new go.Binding('text', 'selectedInCapText', selectedInCap => {
                        //                 return selectedInCap !== 'Select In Cap' ? `In: ${selectedInCap}` : '';
                        //             }),
                        //             new go.Binding('visible', 'selectedInCapText', selectedInCap => {
                        //                 return selectedInCap !== 'Select In Cap';
                        //             })
                        //         )
                        //     )
                        // )
                    )
                )
            )
        );

        this.myDiagram.nodeTemplateMap.add(
            'cableCoreNodeTemplate',
            $(
                go.Node,
                go.Panel.Horizontal,
                {
                    selectable: true,
                    movable: true,
                },
                $(
                    go.Panel,
                    go.Panel.Horizontal,
                    {
                        margin: new go.Margin(0, 0, 0, 0),
                        width: NaN,
                    },
                    // Left connection point
                    $(
                        go.Panel,
                        go.Panel.Vertical,
                        {
                            margin: 0,
                            width: NaN,
                        },
                        // Add border to each Picture by wrapping in a Panel with a Shape as border
                        $(
                            go.Panel,
                            go.Panel.Spot,
                            {},
                            $(go.Shape, 'Rectangle', {
                                desiredSize: new go.Size(24, 24),
                                stroke: '#1976d2',
                                strokeWidth: 1,
                                fill: 'transparent',
                                // geometryString: idx === 0 ? 'M0,0 L0,24 L24,24 L24,0 Z' : 'M0,0 L0,110 L200,110 L200,0',
                            }),
                            $(go.Picture, {
                                source: '/icons/link-variant.svg',
                                width: 20,
                                height: 20,
                                cursor: 'pointer',
                                margin: new go.Margin(0, 0, 0, 0),
                                imageAlignment: go.Spot.Center,
                                imageStretch: go.GraphObject.Uniform,
                                scale: 1,
                                fromLinkable: false,
                                toLinkable: this.hasCircuitEditPermission && !this.readonly,
                                toLinkableDuplicates: false,
                                toMaxLinks: 1,
                                portId: `core-left-side`,
                            })
                        )
                    ),
                    // Central area with core information and dotted line
                    $(
                        go.Panel,
                        go.Panel.Spot,
                        {
                            margin: 0,
                            width: 296,
                            alignment: go.Spot.Center,
                        },
                        // Dotted line from left to right
                        $(go.Shape, {
                            geometryString: 'M0,12 L246,12',
                            stroke: '#666',
                            strokeWidth: 2,
                            strokeDashArray: [5, 5],
                            position: new go.Point(0, 0),
                        }),
                        // Core information panel
                        $(
                            go.Panel,
                            go.Panel.Auto,
                            {
                                margin: 0,
                                alignment: go.Spot.Center,
                                click: (e, obj) => {
                                    // Only allow core interaction with proper permissions
                                    if (!me.hasCircuitEditPermission || me.readonly) return;

                                    const node = obj.part;
                                    const coreId = node?.data?.key;
                                    if (coreId) {
                                        // me.onCoreClick(coreId);
                                    }
                                },
                            },
                            $(go.Shape, 'Rectangle', {
                                fill: '#f9f9f9',
                                stroke: '#ddd',
                                strokeWidth: 1,
                                parameter1: 5,
                            }),
                            $(
                                go.Panel,
                                go.Panel.Horizontal,
                                {
                                    margin: 0,
                                },
                                $(
                                    go.TextBlock,
                                    {
                                        margin: new go.Margin(0, 10, 0, 0),
                                        // font: 'bold 12pt sans-serif',
                                    },
                                    new go.Binding('text', 'text')
                                ),
                                $(go.Picture, {
                                    source: '/icons/delete-empty-red.svg',
                                    width: 16,
                                    height: 16,
                                    cursor: 'pointer',
                                    margin: new go.Margin(0, 0, 0, 0),
                                    imageAlignment: go.Spot.Center,
                                    imageStretch: go.GraphObject.Uniform,
                                    scale: 1,
                                    click: (e: any, obj: any) => {
                                        const node = obj.part;
                                        const coreId = node?.data?.key;
                                        // if (coreId && me.hasCircuitEditPermission && !me.readonly) {
                                        //     // Find the core model and call delete method
                                        //     const allCores = [...me.cables.flatMap(cable => cable.cableCores || []), ...me.noCableCores];
                                        //     const core = allCores.find((c: CableCoreModel) => c.id === coreId);
                                        //     if (core) {
                                        //         me.onDeleteCore(core);
                                        //     }
                                        // }
                                    },
                                })
                            )
                        )
                    ),
                    // Right connection point
                    $(
                        go.Panel,
                        go.Panel.Vertical,
                        {
                            margin: 0,
                            width: NaN,
                        },
                        // Add border to each Picture by wrapping in a Panel with a Shape as border
                        $(
                            go.Panel,
                            go.Panel.Spot,
                            {},
                            $(go.Shape, 'Rectangle', {
                                desiredSize: new go.Size(24, 24),
                                stroke: '#1976d2',
                                strokeWidth: 1,
                                fill: 'transparent',
                                // geometryString:
                                //     idx === 0
                                //         ? // All sides for first index
                                //           'M0,0 L0,24 L24,24 L24,0 Z'
                                //         : // Only left, right, bottom for others
                                //           'M0,0 L0,110 L200,110 L200,0',
                            }),
                            $(go.Picture, {
                                source: '/icons/link-variant.svg',
                                width: 20,
                                height: 20,
                                cursor: 'pointer',
                                margin: new go.Margin(0, 0, 0, 0),
                                imageAlignment: go.Spot.Center,
                                imageStretch: go.GraphObject.Uniform,
                                scale: 1,
                                fromLinkable: this.hasCircuitEditPermission && !this.readonly,
                                toLinkable: false,
                                toLinkableDuplicates: false,
                                toMaxLinks: 1,
                                fromSpot: go.Spot.Right,
                                portId: `core-right-side`,
                            })
                        )
                    )
                )
            )
        );

        this.myDiagram.nodeTemplateMap.add(
            'leftCapsNodeTemplate',
            createCapsNodeTemplate(
                {
                    direction: 'left',
                    readonly: !this.hasCircuitEditPermission || this.readonly,
                },
                {
                    onDeleteCapClick: (capId: string) => {
                        const cap = [...this.inCapsList, ...this.outCapsList].find(x => x.id === capId);
                        if (cap) {
                            this.onDeleteCapClick(cap);
                        }
                    },
                    onCapClick: (capId: string) => {
                        const cap = [...this.inCapsList, ...this.outCapsList].find(x => x.id === capId);
                        if (cap) {
                            this.onCapClick(cap);
                        }
                    },
                }
            )
        );
        this.myDiagram.nodeTemplateMap.add(
            'leftPinsNodeTemplate',
            createPinsNodeTemplate(
                {
                    direction: 'left',
                    readonly: !this.hasCircuitEditPermission || this.readonly,
                },
                {
                    onDeletePinClick: (pinId: string) => {
                        const pin = [...this.inPinsList, ...this.outPinsList].find(x => x.id === pinId);
                        if (pin) {
                            this.onDeletePinClick(pin);
                        }
                    },
                    onPinClick: (pinId: string) => {
                        const pin = [...this.inPinsList, ...this.outPinsList].find(x => x.id === pinId);
                        if (pin) {
                            this.onPinClick(pin);
                        }
                    },
                }
            )
        );
        this.myDiagram.nodeTemplateMap.add(
            'rightCapsNodeTemplate',
            createCapsNodeTemplate(
                {
                    direction: 'right',
                    readonly: !this.hasCircuitEditPermission || this.readonly,
                },
                {
                    onDeleteCapClick: (capId: string) => {
                        const cap = [...this.inCapsList, ...this.outCapsList].find(x => x.id === capId);
                        if (cap) {
                            this.onDeleteCapClick(cap);
                        }
                    },
                    onCapClick: (capId: string) => {
                        const cap = [...this.inCapsList, ...this.outCapsList].find(x => x.id === capId);
                        if (cap) {
                            this.onCapClick(cap);
                        }
                    },
                }
            )
        );
        this.myDiagram.nodeTemplateMap.add(
            'rightPinsNodeTemplate',
            createPinsNodeTemplate(
                {
                    direction: 'right',
                    readonly: !this.hasCircuitEditPermission || this.readonly,
                },
                {
                    onDeletePinClick: (pinId: string) => {
                        const pin = [...this.inPinsList, ...this.outPinsList].find(x => x.id === pinId);
                        if (pin) {
                            this.onDeletePinClick(pin);
                        }
                    },
                    onPinClick: (pinId: string) => {
                        const pin = [...this.inPinsList, ...this.outPinsList].find(x => x.id === pinId);
                        if (pin) {
                            this.onPinClick(pin);
                        }
                    },
                }
            )
        );
        // this.myDiagram.nodeTemplateMap.add('leftConnectorPinsNodeTemplate', leftConnectorPinsNodeTemplate);
        // this.myDiagram.nodeTemplateMap.add('rightConnectorPinsNodeTemplate', rightConnectorPinsNodeTemplate);
        this.myDiagram.nodeTemplateMap.add('afterPinAddButtonTemplate', afterPinAddButtonTemplate);

        const me = this;

        /*-------------------------------------------------------------------------------------------------*/
        /*-------------------------------------------------------------------------------------------------*/
        /*-------------------------------------------------------------------------------------------------*/
        /*-------------------------------------------------------------------------------------------------*/
        /*-------------------------------------------------------------------------------------------------*/
        const outerBoxGroupTemplate = $(
            go.Group,
            go.Panel.Auto,
            {
                layout: $(go.GridLayout, {
                    wrappingColumn: 1, // Two columns for Cap In, Pin In and Pin Out, Cap Out
                    alignment: go.GridLayout.Position,
                    cellSize: new go.Size(1, 1),
                    spacing: new go.Size(0, 1),
                }),
                // width: 1010,
                isSubGraphExpanded: true,
                computesBoundsAfterDrag: true,
                handlesDragDropForMembers: true,
                movable: false,
            },
            $(
                go.Panel,
                go.Panel.Auto,
                $(go.Shape, 'Rectangle', {
                    fill: 'rgba(200, 200, 200, 0.33)',
                    margin: 0,
                    stroke: '#cccccc',
                    strokeWidth: 1,
                }),
                new go.Placeholder({padding: 0}) // Add padding inside the rectangle
            )
        );

        const rowOneGroupTemplate = $(
            go.Group,
            go.Panel.Horizontal,
            {
                layout: $(go.GridLayout, {
                    wrappingColumn: 11,
                    alignment: go.GridLayout.Position,
                    wrappingWidth: 3300, // Adjust this value based on your layout needs
                    spacing: new go.Size(0, 0),
                    cellSize: new go.Size(1, 1),
                }),
                isSubGraphExpanded: true,
                computesBoundsAfterDrag: true,
                handlesDragDropForMembers: true,
                movable: false,
            },
            $(
                go.Panel,
                go.Panel.Auto,
                $(go.Shape, 'Rectangle', {
                    fill: 'rgba(229, 190, 190, 0.33)',
                    margin: 0,
                    stroke: '#000',
                    strokeWidth: 0,
                    // width: 855,
                    // height: 200,
                }),
                new go.Placeholder({padding: 0})
            )
        );

        const colOneGroupTemplate = $(
            go.Group,
            go.Panel.Vertical,
            {
                layout: $(go.GridLayout, {
                    wrappingColumn: 1,
                    alignment: go.GridLayout.Position,
                    spacing: new go.Size(0, 0),
                    cellSize: new go.Size(1, 1),
                    comparer: (a: go.Part, b: go.Part) => {
                        const aOrder = a.data.sortOrder ?? 0;
                        const bOrder = b.data.sortOrder ?? 0;
                        return aOrder - bOrder;
                    },
                }),
                isSubGraphExpanded: true,
                computesBoundsAfterDrag: true,
                handlesDragDropForMembers: true,
                movable: false,
            },
            $(go.TextBlock, {font: 'bold 14pt sans-serif', textAlign: 'center'}, new go.Binding('text', 'text')),
            $(
                go.Panel,
                go.Panel.Auto,
                {
                    // alignment: go.Spot.Top,
                },
                $(
                    go.Panel,
                    go.Panel.Auto,
                    {
                        alignment: go.Spot.Top,
                    },
                    $(go.Shape, 'Rectangle', {
                        fill: 'rgba(162, 205, 171, 0.33)',
                        margin: 0,
                        stroke: '#000',
                        strokeWidth: 1,
                        width: 162, // <-- Set your desired width for colOne
                        height: 540,
                    }),
                    new go.Placeholder({padding: 0, alignment: go.Spot.Top})
                )
            )
        );

        const colThreeGroupTemplate = $(
            go.Group,
            go.Panel.Vertical,
            {
                layout: $(go.GridLayout, {
                    wrappingColumn: 1,
                    alignment: go.GridLayout.Position,
                    spacing: new go.Size(0, 0),
                    cellSize: new go.Size(1, 1),
                    comparer: (a: go.Part, b: go.Part) => {
                        const aOrder = a.data.sortOrder ?? 0;
                        const bOrder = b.data.sortOrder ?? 0;
                        return aOrder - bOrder;
                    },
                }),
                isSubGraphExpanded: true,
                computesBoundsAfterDrag: true,
                handlesDragDropForMembers: true,
                movable: false,
                mouseDragEnter: (e, group: any) => {
                    group.isHighlighted = true;
                },
                mouseDragLeave: (e, group: any) => {
                    group.isHighlighted = false;
                },
                mouseDrop: (e, group: any) => {
                    // Check permissions before allowing drag and drop
                    if (!me.hasCircuitEditPermission || me.readonly) return;

                    const tool = e.diagram.toolManager.draggingTool;
                    const draggedParts = tool.draggedParts;
                    if (draggedParts) {
                        draggedParts.each(part => {
                            if (part instanceof go.Node) {
                                part.containingGroup = group as any;
                            }
                        });
                    }
                    group.isHighlighted = false;
                    group.layout.invalidateLayout(); // Trigger layout rearrangement

                    // Sort nodeDataArray data of the group based on sortOrder
                    const groupKey = group.data.key;
                    const groupNodes = me.myDiagram!.model.nodeDataArray.filter((node: any) => node.group === groupKey);

                    groupNodes.sort((a: any, b: any) => {
                        const nodeA = me.myDiagram!.findNodeForData(a);
                        const nodeB = me.myDiagram!.findNodeForData(b);
                        return (nodeA?.location.y ?? 0) - (nodeB?.location.y ?? 0);
                    });

                    groupNodes.forEach((node: any, index: number) => {
                        // node.sortOrder = index; // Update sortOrder locally
                        e.diagram.model.setDataProperty(node, 'sortOrder', index);
                    });
                },
            },
            $(go.TextBlock, {font: 'bold 14pt sans-serif', textAlign: 'center'}, new go.Binding('text', 'text')),
            $(
                go.Panel,
                go.Panel.Auto,
                {
                    // alignment: go.Spot.Top,
                },
                $(
                    go.Panel,
                    go.Panel.Auto,
                    {
                        alignment: go.Spot.Top,
                    },
                    $(go.Shape, 'Rectangle', {
                        fill: 'rgba(162, 205, 171, 0.33)',
                        margin: 0,
                        stroke: '#000',
                        strokeWidth: 1,
                        width: 350, // <-- Set your desired width for colOne
                        // height: 540,
                    }),
                    new go.Placeholder({padding: 0, alignment: go.Spot.Top})
                )
            )
        );

        // const colConnectorGroupTemplate = $(
        //     go.Group,
        //     go.Panel.Vertical,
        //     {
        //         layout: $(go.GridLayout, {
        //             wrappingColumn: 1,
        //             alignment: go.GridLayout.Position,
        //             spacing: new go.Size(0, 0),
        //             cellSize: new go.Size(1, 1),
        //             comparer: (a: go.Part, b: go.Part) => {
        //                 const aOrder = a.data.sortOrder ?? 0;
        //                 const bOrder = b.data.sortOrder ?? 0;
        //                 return aOrder - bOrder;
        //             },
        //         }),
        //         isSubGraphExpanded: true,
        //         computesBoundsAfterDrag: true,
        //         handlesDragDropForMembers: true,
        //         movable: false,
        //     },
        //     $(go.TextBlock, {font: 'bold 14pt sans-serif', textAlign: 'center'}, new go.Binding('text', 'text')),
        //     $(
        //         go.Panel,
        //         go.Panel.Auto,
        //         {
        //             // alignment: go.Spot.Top,
        //         },
        //         $(
        //             go.Panel,
        //             go.Panel.Auto,
        //             {
        //                 alignment: go.Spot.Top,
        //             },
        //             $(go.Shape, 'Rectangle', {
        //                 fill: 'rgba(162, 205, 171, 0.33)',
        //                 margin: 0,
        //                 stroke: '#000',
        //                 strokeWidth: 1,
        //                 width: 232, // <-- Set your desired width for colOne
        //                 height: 540,
        //             }),
        //             new go.Placeholder({padding: 0, alignment: go.Spot.Top})
        //         )
        //     )
        // );

        this.myDiagram.groupTemplateMap.add('OUT_BOX', outerBoxGroupTemplate);
        this.myDiagram.groupTemplateMap.add('ROW_1', rowOneGroupTemplate);

        this.myDiagram.groupTemplateMap.add('ROW_1_COL_1', colOneGroupTemplate);
        this.myDiagram.groupTemplateMap.add('ROW_1_COL_2', colOneGroupTemplate);
        this.myDiagram.groupTemplateMap.add('ROW_1_COL_3', colThreeGroupTemplate);
        this.myDiagram.groupTemplateMap.add('ROW_1_COL_4', colOneGroupTemplate);
        // this.myDiagram.groupTemplateMap.add('ROW_1_COL_5', colConnectorGroupTemplate);
        // this.myDiagram.groupTemplateMap.add('ROW_1_COL_6', colOneGroupTemplate);
        // this.myDiagram.groupTemplateMap.add('ROW_1_COL_7', colConnectorGroupTemplate);
        // this.myDiagram.groupTemplateMap.add('ROW_1_COL_8', colOneGroupTemplate);
        this.myDiagram.groupTemplateMap.add('ROW_1_COL_5', colOneGroupTemplate);
        this.myDiagram.groupTemplateMap.add('ROW_1_COL_6', colOneGroupTemplate);
        this.myDiagram.groupTemplateMap.add('ROW_1_COL_7', colOneGroupTemplate);
        this.myDiagram.groupTemplateMap.add('ROW_1_COL_8', colOneGroupTemplate);
        this.myDiagram.groupTemplateMap.add('ROW_1_COL_9', colThreeGroupTemplate);
        this.myDiagram.groupTemplateMap.add('ROW_1_COL_10', colOneGroupTemplate);
        this.myDiagram.groupTemplateMap.add('ROW_1_COL_11', colOneGroupTemplate);

        /*-------------------------------------------------------------------------------------------------*/
        /*-------------------------------------------------------------------------------------------------*/
        /*-------------------------------------------------------------------------------------------------*/
        /*-------------------------------------------------------------------------------------------------*/
        /*-------------------------------------------------------------------------------------------------*/

        const createGroupTemplate = (groupName: string) => {
            return $(
                go.Group,
                go.Panel.Vertical,
                {
                    layout: $(go.GridLayout, {
                        wrappingColumn: 1, // Ensure nodes are aligned into a single column
                        alignment: go.GridLayout.Position,
                        cellSize: new go.Size(1, 1),
                        spacing: new go.Size(0, 1), // Adjust vertical spacing between nodes
                        comparer: (a: go.Part, b: go.Part) => {
                            const aOrder = a.data.sortOrder ?? 0;
                            const bOrder = b.data.sortOrder ?? 0;
                            return aOrder - bOrder;
                        },
                    }),
                    isSubGraphExpanded: true,
                    computesBoundsAfterDrag: true,
                    handlesDragDropForMembers: true,
                    mouseDragEnter: (e, group: any) => {
                        group.isHighlighted = true;
                    },
                    mouseDragLeave: (e, group: any) => {
                        group.isHighlighted = false;
                    },
                    mouseDrop: (e, group: any) => {
                        const tool = e.diagram.toolManager.draggingTool;
                        const draggedParts = tool.draggedParts;
                        if (draggedParts) {
                            draggedParts.each(part => {
                                if (part instanceof go.Node) {
                                    part.containingGroup = group as any;
                                }
                            });
                        }
                        group.isHighlighted = false;
                        group.layout.invalidateLayout(); // Trigger layout rearrangement

                        // Sort nodeDataArray data of the group based on sortOrder
                        const groupKey = group.data.key;
                        const groupNodes = me.myDiagram!.model.nodeDataArray.filter((node: any) => node.group === groupKey);

                        groupNodes.sort((a: any, b: any) => {
                            const nodeA = me.myDiagram!.findNodeForData(a);
                            const nodeB = me.myDiagram!.findNodeForData(b);
                            return (nodeA?.location.y ?? 0) - (nodeB?.location.y ?? 0);
                        });

                        groupNodes.forEach((node: any, index: number) => {
                            // node.sortOrder = index; // Update sortOrder locally
                            e.diagram.model.setDataProperty(node, 'sortOrder', index);
                        });
                    },
                },
                $(go.TextBlock, {font: 'bold 12pt sans-serif'}, new go.Binding('text', 'text')),
                $(
                    go.Panel,
                    go.Panel.Auto,
                    $(
                        go.Panel,
                        go.Panel.Auto,
                        $(go.Shape, 'Rectangle', {
                            fill: 'rgba(200, 200, 200, 0.33)',
                            margin: 0,
                            stroke: '#cccccc',
                            strokeWidth: 2,
                        }),
                        new go.Placeholder({padding: 5}) // Add padding inside the rectangle
                    )
                )
            );
        };

        // const connectorPinsGroupTemplate = $(
        //     go.Group,
        //     go.Panel.Vertical,
        //     {
        //         layout: $(go.GridLayout, {
        //             wrappingColumn: 1, // Ensure nodes are aligned into a single column
        //             alignment: go.GridLayout.Position,
        //             cellSize: new go.Size(1, 1),
        //             spacing: new go.Size(0, 1), // Adjust vertical spacing between nodes
        //             comparer: (a: go.Part, b: go.Part) => {
        //                 const aOrder = a.data.sortOrder ?? 0;
        //                 const bOrder = b.data.sortOrder ?? 0;
        //                 return aOrder - bOrder;
        //             },
        //         }),
        //         isSubGraphExpanded: true,
        //         computesBoundsAfterDrag: true,
        //         handlesDragDropForMembers: true,
        //         mouseDragEnter: (e, group: any) => {
        //             group.isHighlighted = true;
        //         },
        //         mouseDragLeave: (e, group: any) => {
        //             group.isHighlighted = false;
        //         },
        //         mouseDrop: (e, group: any) => {
        //             const tool = e.diagram.toolManager.draggingTool;
        //             const draggedParts = tool.draggedParts;
        //             if (draggedParts) {
        //                 draggedParts.each(part => {
        //                     if (part instanceof go.Node) {
        //                         part.containingGroup = group as any;
        //                     }
        //                 });
        //             }
        //             group.isHighlighted = false;
        //             group.layout.invalidateLayout(); // Trigger layout rearrangement

        //             // Sort nodeDataArray data of the group based on sortOrder
        //             const groupKey = group.data.key;
        //             const groupNodes = me.myDiagram!.model.nodeDataArray.filter((node: any) => node.group === groupKey);

        //             groupNodes.sort((a: any, b: any) => {
        //                 const nodeA = me.myDiagram!.findNodeForData(a);
        //                 const nodeB = me.myDiagram!.findNodeForData(b);
        //                 return (nodeA?.location.y ?? 0) - (nodeB?.location.y ?? 0);
        //             });

        //             groupNodes.forEach((node: any, index: number) => {
        //                 // node.sortOrder = index; // Update sortOrder locally
        //                 e.diagram.model.setDataProperty(node, 'sortOrder', index);
        //             });
        //         },
        //     },
        //     $(go.TextBlock, {font: 'bold 12pt sans-serif'}, new go.Binding('text', 'text')),
        //     $(
        //         go.Panel,
        //         go.Panel.Auto,
        //         $(
        //             go.Panel,
        //             go.Panel.Auto,
        //             $(go.Shape, 'Rectangle', {
        //                 fill: 'rgba(200, 200, 200, 0.33)',
        //                 margin: 0,
        //                 stroke: '#cccccc',
        //                 strokeWidth: 2,
        //             }),
        //             new go.Placeholder({padding: 5}) // Add padding inside the rectangle
        //         )
        //     )
        // );

        // Create group templates for each group
        // const inCablesGroupTemplate = createGroupTemplate('IN_CABLES');
        const inCapsGroupTemplate = createGroupTemplate('IN_CAPS');
        const inPinsGroupTemplate = createGroupTemplate('IN_PINS');

        // const outCablesGroupTemplate = createGroupTemplate('OUT_CABLES');
        const outCapsGroupTemplate = createGroupTemplate('OUT_CAPS');
        const outPinsGroupTemplate = createGroupTemplate('OUT_PINS');

        // Assign group templates to the groupTemplateMap
        // this.myDiagram.groupTemplateMap.add('IN_CABLES', inCablesGroupTemplate);
        this.myDiagram.groupTemplateMap.add('IN_CAPS', inCapsGroupTemplate);
        this.myDiagram.groupTemplateMap.add('IN_PINS', inPinsGroupTemplate);
        // this.myDiagram.groupTemplateMap.add('CONNECTOR_PINS_IN', connectorPinsGroupTemplate);
        // this.myDiagram.groupTemplateMap.add('CONNECTOR_PINS_OUT', connectorPinsGroupTemplate);
        // this.myDiagram.groupTemplateMap.add('OUT_CABLES', outCablesGroupTemplate);
        this.myDiagram.groupTemplateMap.add('OUT_CAPS', outCapsGroupTemplate);
        this.myDiagram.groupTemplateMap.add('OUT_PINS', outPinsGroupTemplate);

        const createSubGridLayout = () => {
            return $(go.GridLayout, {
                wrappingColumn: 2, // Two columns for Cap In, Pin In and Pin Out, Cap Out
                alignment: go.GridLayout.Position,
                cellSize: new go.Size(1, 1),
                spacing: new go.Size(20, 10),
            });
        };

        // const wireInGroupTemplate = $(
        //     go.Group,
        //     go.Panel.Vertical,
        //     {
        //         layout: createSubGridLayout(),
        //         isSubGraphExpanded: true,
        //         computesBoundsAfterDrag: true,
        //         handlesDragDropForMembers: true,
        //     },
        //     $(go.TextBlock, {font: 'bold 14pt sans-serif', textAlign: 'center'}, new go.Binding('text', 'text')),
        //     $(
        //         go.Panel,
        //         go.Panel.Auto,
        //         $(go.Shape, 'Rectangle', {
        //             fill: 'rgba(243, 243, 243, 0.33)',
        //             margin: 0,
        //             stroke: '#aaaaaa',
        //             strokeWidth: 2,
        //         }),
        //         new go.Placeholder({padding: 5})
        //     )
        // );

        // const connectorBlockTemplate = $(
        //     go.Group,
        //     go.Panel.Vertical,
        //     {
        //         layout: $(go.GridLayout, {
        //             wrappingColumn: 2,
        //             alignment: go.GridLayout.Position,
        //             cellSize: new go.Size(1, 1),
        //             spacing: new go.Size(20, 10),
        //         }),
        //         isSubGraphExpanded: true,
        //         computesBoundsAfterDrag: true,
        //         handlesDragDropForMembers: true,
        //     },
        //     $(go.TextBlock, {font: 'bold 14pt sans-serif', textAlign: 'center'}, new go.Binding('text', 'text')),
        //     $(
        //         go.Panel,
        //         go.Panel.Auto,
        //         $(go.Shape, 'Rectangle', {
        //             fill: 'rgba(243, 243, 243, 0.33)',
        //             margin: 0,
        //             stroke: '#aaaaaa',
        //             strokeWidth: 2,
        //         }),
        //         new go.Placeholder({padding: 5})
        //     )
        // );

        // const wireOutGroupTemplate = $(
        //     go.Group,
        //     go.Panel.Vertical,
        //     {
        //         layout: createSubGridLayout(),
        //         isSubGraphExpanded: true,
        //         computesBoundsAfterDrag: true,
        //         handlesDragDropForMembers: true,
        //     },
        //     $(go.TextBlock, {font: 'bold 14pt sans-serif', textAlign: 'center'}, new go.Binding('text', 'text')),
        //     $(
        //         go.Panel,
        //         go.Panel.Auto,
        //         $(go.Shape, 'Rectangle', {
        //             fill: 'rgba(243, 243, 243, 0.33)',
        //             margin: 0,
        //             stroke: '#aaaaaa',
        //             strokeWidth: 2,
        //         }),
        //         new go.Placeholder({padding: 5})
        //     )
        // );

        // this.myDiagram.groupTemplateMap.add('WIRE_IN', wireInGroupTemplate);
        // this.myDiagram.groupTemplateMap.add('CONNECTOR_BLOCK', connectorBlockTemplate);
        // this.myDiagram.groupTemplateMap.add('WIRE_OUT', wireOutGroupTemplate);

        const gridLayout = new go.GridLayout();
        gridLayout.wrappingColumn = 1;
        gridLayout.wrappingWidth = 3300;
        gridLayout.isViewportSized = false;
        gridLayout.alignment = go.GridLayout.Position;
        gridLayout.cellSize = new go.Size(1, 1);
        gridLayout.spacing = new go.Size(30, 10);
        this.myDiagram.layout = gridLayout;

        this.myDiagram.addDiagramListener('LinkDrawn', e => {
            const link = e.subject as go.Link;
            const fromNode = link.fromNode as go.Node;
            const toNode = link.toNode as go.Node;

            if (!fromNode || !toNode) return;
            if (
                (fromNode.data?.type === 'cap-in' && toNode.data?.type === 'cap-in') ||
                (fromNode.data?.type === 'cap-out' && toNode.data?.type === 'cap-out') ||
                (fromNode.data?.type === 'pin-in' && toNode.data?.type === 'pin-in') ||
                (fromNode.data?.type === 'pin-out' && toNode.data?.type === 'pin-out') ||
                (fromNode.data?.type === 'connector-pin-in' && toNode.data?.type === 'connector-pin-in') ||
                (fromNode.data?.type === 'connector-pin-out' && toNode.data?.type === 'connector-pin-out')
            ) {
                e.diagram.remove(link);
                return;
            }

            if (
                (fromNode.data?.type === 'cap-in' && toNode.data?.type === 'pin-in') ||
                (fromNode.data?.type === 'cap-out' && toNode.data?.type === 'pin-out')
            ) {
                this.onPinCapConnectoin(toNode.data.key, fromNode.data.key);
            } else if (
                (fromNode.data?.type === 'pin-in' && toNode.data?.type === 'cap-in') ||
                (fromNode.data?.type === 'pin-out' && toNode.data?.type === 'cap-out')
            ) {
                this.onPinCapConnectoin(fromNode.data.key, toNode.data.key);
            }
            // Handle cable-cap connections
            else if (
                (fromNode.data?.type === 'cable-in' || fromNode.data?.type === 'cable-out') &&
                (toNode.data?.type === 'cap-in' || toNode.data?.type === 'cap-out') &&
                ((link.fromPortId === 'cable-left-side' && link.toPortId === 'left-cap-right-side') ||
                    (link.fromPortId === 'cable-right-side' && link.toPortId === 'right-cap-left-side'))
            ) {
                // Determine cable side based on port ID
                const cableSide = link.fromPortId === 'cable-left-side' ? 'left' : 'right';
                this.onCableCapConnection(fromNode.data.key, toNode.data.key, cableSide);
            } else if (
                (toNode.data?.type === 'cable-in' || toNode.data?.type === 'cable-out') &&
                (fromNode.data?.type === 'cap-in' || fromNode.data?.type === 'cap-out') &&
                ((link.toPortId === 'cable-left-side' && link.fromPortId === 'right-cap-left-side') ||
                    (link.toPortId === 'cable-right-side' && link.fromPortId === 'left-cap-right-side'))
            ) {
                // Determine cable side based on port ID
                const cableSide = link.toPortId === 'cable-left-side' ? 'left' : 'right';
                this.onCableCapConnection(toNode.data.key, fromNode.data.key, cableSide);
            }
            // Pin-to-pin connections
            else if (fromNode.data?.type === 'pin-in' && toNode.data?.type === 'pin-out') {
                this.onPinPinConnection(fromNode.data.key, toNode.data.key);
            } else if (fromNode.data?.type === 'pin-out' && toNode.data?.type === 'pin-in') {
                this.onPinPinConnection(toNode.data.key, fromNode.data.key);
            } else {
                e.diagram.remove(link);
            }
        });

        this.myDiagram.model = new go.GraphLinksModel({
            linkFromPortIdProperty: 'fromPort',
            linkToPortIdProperty: 'toPort',
            nodeDataArray: [
                {key: 'out-box', text: 'Outer Box', isGroup: true, category: 'OUT_BOX'},
                {key: 'row-1', text: 'Row One', isGroup: true, category: 'ROW_1', group: 'out-box'},

                {key: 'col-in-caps', text: 'In Caps', isGroup: true, category: 'ROW_1_COL_1', group: 'row-1'},
                {key: 'col-2', text: '', isGroup: true, category: 'ROW_1_COL_2', group: 'row-1'},
                {key: 'col-in-cables', text: 'In Cables', isGroup: true, category: 'ROW_1_COL_3', group: 'row-1'},
                {key: 'col-4', text: '', isGroup: true, category: 'ROW_1_COL_4', group: 'row-1'},
                {key: 'col-in-pins', text: 'In Pins', isGroup: true, category: 'ROW_1_COL_5', group: 'row-1'},
                {key: 'col-6', text: '', isGroup: true, category: 'ROW_1_COL_6', group: 'row-1'},
                // {key: 'col-5', text: 'IN Connector Pins', isGroup: true, category: 'ROW_1_COL_5', group: 'row-1'},
                // {key: 'col-6', text: '', isGroup: true, category: 'ROW_1_COL_6', group: 'row-1'},
                // {key: 'col-7', text: 'Out Connector Pins', isGroup: true, category: 'ROW_1_COL_7', group: 'row-1'},
                // {key: 'col-8', text: '', isGroup: true, category: 'ROW_1_COL_8', group: 'row-1'},
                {key: 'col-out-pins', text: 'Out Pins', isGroup: true, category: 'ROW_1_COL_7', group: 'row-1'},
                {key: 'col-8', text: '', isGroup: true, category: 'ROW_1_COL_8', group: 'row-1'},
                {key: 'col-out-cables', text: 'Out Cables', isGroup: true, category: 'ROW_1_COL_9', group: 'row-1'},
                {key: 'col-10', text: '', isGroup: true, category: 'ROW_1_COL_10', group: 'row-1'},
                {key: 'col-out-caps', text: 'Out Caps', isGroup: true, category: 'ROW_1_COL_11', group: 'row-1'},

                // {key: 'WIRE_IN', text: 'Wire In', isGroup: true, category: 'WIRE_IN'},
                // {key: 'CONNECTOR_BLOCK', text: 'Connector Block', isGroup: true, category: 'CONNECTOR_BLOCK'},
                // {key: 'WIRE_OUT', text: 'Wire Out', isGroup: true, category: 'WIRE_OUT'},

                // {key: 'IN_CAPS', text: 'IN Caps', isGroup: true, category: 'IN_CAPS', group: 'WIRE_IN'},
                // {key: 'IN_PINS', text: 'IN Pins', isGroup: true, category: 'IN_PINS', group: 'WIRE_IN'},
                // {key: 'CONNECTOR_PINS_IN', text: 'Connector Pins In', isGroup: true, category: 'CONNECTOR_PINS_IN', group: 'CONNECTOR_BLOCK'},
                // {key: 'CONNECTOR_PINS_OUT', text: 'Connector Pins Out', isGroup: true, category: 'CONNECTOR_PINS_OUT', group: 'CONNECTOR_BLOCK'},
                // {key: 'OUT_PINS', text: 'OUT Pins', isGroup: true, category: 'OUT_PINS', group: 'WIRE_OUT'},
                // {key: 'OUT_CAPS', text: 'OUT Caps', isGroup: true, category: 'OUT_CAPS', group: 'WIRE_OUT'},
            ],
            linkDataArray: [],
        });
        this.updateNodeDataArray();
    }

    private updateNodeDataArray(): void {
        const nodeDataArray: any[] = [];
        const linksDataArray: any[] = [];

        for (const cap of [...this.inCapsList, ...this.outCapsList]) {
            const inPinIds = this.getInPinIdsFromCap(cap.id);
            const outPinIds = this.getOutPinIdsFromCap(cap.id);

            nodeDataArray.push({
                key: cap.id,
                text: cap.title,
                group: cap.capDirection === CAP_DIRECTION.IN ? 'col-in-caps' : 'col-out-caps',
                category: cap.capDirection === CAP_DIRECTION.IN ? 'leftCapsNodeTemplate' : 'rightCapsNodeTemplate',
                sortOrder: cap.sortOrder ?? 0,
                isActive: cap.isActive,
                pinIds: [...inPinIds, ...outPinIds],
                type: cap.capDirection === CAP_DIRECTION.IN ? 'cap-in' : 'cap-out',
            });

            if (cap.capDirection === CAP_DIRECTION.IN) {
                for (const pinId of inPinIds) {
                    // const isLinked = linksDataArray.some(link => {
                    //     return (link.from === cap.id && link.to === pinId) || (link.from === pinId && link.to === cap.id);
                    // });
                    // if (!isLinked) {
                    // }
                    // linksDataArray.push({
                    //     to: pinId,
                    //     from: cap.id,
                    //     toPort: 'left-pin-left-side',
                    //     fromPort: 'left-cap-right-side',
                    // });
                }
            } else {
                for (const pinId of outPinIds) {
                    // const isLinked = linksDataArray.some(link => {
                    //     return (link.from === cap.id && link.to === pinId) || (link.from === pinId && link.to === cap.id);
                    // });
                    // if (!isLinked) {
                    // }
                    // linksDataArray.push({
                    //     to: pinId,
                    //     from: cap.id,
                    //     toPort: 'right-pin-right-side',
                    //     fromPort: 'right-cap-left-side',
                    // });
                }
            }
        }

        for (const cable of this.inCablesList) {
            nodeDataArray.push({
                key: cable.id,
                text: cable.title,
                group: 'col-in-cables',
                category: 'cableNodeGroupTemplate',
                sortOrder: cable.sortOrder ?? 0,
                isActive: cable.isActive,
                type: 'cable-in',
                isGroup: true,
                selectedInCapText: '',
                selectedOutCapText: '',
            });

            for (const core of cable.cableCores ?? []) {
                nodeDataArray.push({
                    key: core.id,
                    text: core.title,
                    group: cable.id,
                    category: 'cableCoreNodeTemplate',
                    sortOrder: core.sortOrder ?? 0,
                    isActive: core.isActive,
                    type: 'cable-core',
                });
            }
        }

        for (const cable of this.outCablesList) {
            nodeDataArray.push({
                key: cable.id,
                text: cable.title,
                group: 'col-out-cables',
                category: 'cableNodeGroupTemplate',
                sortOrder: cable.sortOrder ?? 0,
                isActive: cable.isActive,
                type: 'cable-out',
                isGroup: true,
                selectedInCapText: '',
                selectedOutCapText: '',
            });

            for (const core of cable.cableCores ?? []) {
                nodeDataArray.push({
                    key: core.id,
                    text: core.title,
                    group: cable.id,
                    category: 'cableCoreNodeTemplate',
                    sortOrder: core.sortOrder ?? 0,
                    isActive: core.isActive,
                    type: 'cable-core',
                });
            }
        }

        for (const pin of [...this.inPinsList, ...this.outPinsList]) {
            nodeDataArray.push({
                key: pin.id,
                text: pin.title,
                group: pin.pinDirection === PIN_DIRECTION.IN ? 'col-in-pins' : 'col-out-pins',
                category: pin.pinDirection === PIN_DIRECTION.IN ? 'leftPinsNodeTemplate' : 'rightPinsNodeTemplate',
                sortOrder: pin.sortOrder ?? 0,
                isActive: pin.isActive,
                type: pin.pinDirection === PIN_DIRECTION.IN ? 'pin-in' : 'pin-out',
            });
        }

        // for (const pin of [...this.inConnectorPinsList, ...this.outConnectorPinsList]) {
        //     nodeDataArray.push({
        //         key: pin.id,
        //         text: pin.title,
        //         group: pin.pinDirection === PIN_DIRECTION.IN ? 'col-5' : 'col-7',
        //         category: pin.pinDirection === PIN_DIRECTION.IN ? 'leftConnectorPinsNodeTemplate' : 'rightConnectorPinsNodeTemplate',
        //         sortOrder: pin.sortOrder ?? 0,
        //         isActive: pin.isActive,
        //         type: pin.pinDirection === PIN_DIRECTION.IN ? 'connector-pin-in' : 'connector-pin-out',
        //     });
        // }

        for (const pin of [...this.inPinsList, ...this.outPinsList]) {
            // if (pin.equipmentCapId) {
            //     linksDataArray.push({
            //         to: pin.id,
            //         from: pin.equipmentCapId,
            //         toPort: pin.pinDirection === PIN_DIRECTION.IN ? 'left-pin-left-side' : 'right-pin-right-side',
            //         fromPort: pin.pinDirection === PIN_DIRECTION.IN ? 'left-cap-right-side' : 'right-cap-left-side',
            //     });
            // }
            if (pin.connectedPinId) {
                const isLinked = linksDataArray.some(link => {
                    return (link.from === pin.id && link.to === pin.connectedPinId) || (link.from === pin.connectedPinId && link.to === pin.id);
                });
                if (isLinked) continue;
                linksDataArray.push({
                    from: pin.id,
                    to: pin.connectedPinId,
                    fromPort: pin.pinDirection === PIN_DIRECTION.IN ? 'left-pin-right-side' : 'right-pin-left-side',
                    toPort: pin.pinDirection === PIN_DIRECTION.IN ? 'right-pin-left-side' : 'left-pin-right-side',
                });
            }
        }

        for (const cable of [...this.inCablesList, ...this.outCablesList]) {
            for (const core of cable.cableCores ?? []) {
                if (core.equipmentInPin) {
                    linksDataArray.push({
                        from: core.equipmentInPinId,
                        to: core.id,
                        fromPort: 'left-pin-left-side',
                        toPort: 'core-right-side',
                    });
                }
                if (core.equipmentOutPinId) {
                    linksDataArray.push({
                        to: core.equipmentOutPinId,
                        from: core.id,
                        toPort: 'right-pin-right-side',
                        fromPort: 'core-left-side',
                    });
                }
            }

            if (cable.equipmentInCapId) {
                linksDataArray.push({
                    from: cable.equipmentInCapId,
                    to: cable.id,
                    fromPort: 'left-cap-right-side',
                    toPort: 'cable-left-side',
                });
            }
            if (cable.equipmentOutCapId) {
                linksDataArray.push({
                    to: cable.equipmentOutCapId,
                    from: cable.id,
                    toPort: 'right-cap-left-side',
                    fromPort: 'cable-right-side',
                });
            }
        }

        // for (const pin of this.inConnectorPinsList) {
        //     if (pin.equipmentConnectorPinIds) {
        //         for (const equipmentPinId of pin.equipmentConnectorPinIds) {
        //             linksDataArray.push({
        //                 to: equipmentPinId,
        //                 from: pin.id,
        //                 fromPort: 'left-connector-pin-right-side',
        //                 toPort: 'right-connector-pin-left-side',
        //             });
        //         }
        //     }

        //     if (pin.equipmentPinIds?.length) {
        //         for (const equipmentPinId of pin.equipmentPinIds) {
        //             linksDataArray.push({
        //                 to: pin.id,
        //                 from: equipmentPinId,
        //                 toPort: pin.pinDirection === PIN_DIRECTION.IN ? 'left-connector-pin-left-side' : 'right-connector-pin-left-side',
        //                 fromPort: pin.pinDirection === PIN_DIRECTION.IN ? 'left-pin-right-side' : 'right-pin-left-side',
        //             });
        //         }
        //     }
        // }

        // for (const pin of this.outConnectorPinsList) {
        //     if (pin.equipmentPinIds?.length) {
        //         for (const equipmentPinId of pin.equipmentPinIds) {
        //             linksDataArray.push({
        //                 to: pin.id,
        //                 from: equipmentPinId,
        //                 toPort: 'right-connector-pin-right-side',
        //                 fromPort: 'right-pin-left-side',
        //             });
        //         }
        //     }
        // }

        if (this.myDiagram) {
            // Only manage dynamic nodes/groups here: regular nodes and cable groups.
            const isDynamic = (nd: any) => !nd.isGroup || nd.category === 'cableNodeGroupTemplate';

            const removedNodes = this.myDiagram.model.nodeDataArray.filter(node => {
                return isDynamic(node) && !nodeDataArray.some(n => n.key === node.key);
            });
            const newAddedNodes = nodeDataArray.filter(node => {
                return isDynamic(node) && !this.myDiagram!.model.nodeDataArray.some(n => n.key === node.key);
            });

            const nodesToUpdate = this.myDiagram.model.nodeDataArray.filter(node => {
                return isDynamic(node) && nodeDataArray.some(n => n.key === node.key);
            });

            for (const node of removedNodes) {
                this.myDiagram.model.removeNodeData(node);
            }

            for (const node of nodesToUpdate) {
                const newNode = nodeDataArray.find(n => n.key === node.key);
                if (newNode) {
                    this.myDiagram.model.setDataProperty(node, 'text', newNode.text);
                    this.myDiagram.model.setDataProperty(node, 'sortOrder', newNode.sortOrder);
                    this.myDiagram.model.setDataProperty(node, 'isActive', newNode.isActive);
                }
            }

            setTimeout(() => {
                for (const node of newAddedNodes) {
                    this.myDiagram!.model.addNodeData(node);
                }

                this.addAfterPinsNodes();
            }, 100);

            console.log(linksDataArray);

            this.myDiagram.model.startTransaction('links');
            (this.myDiagram.model as go.GraphLinksModel).linkDataArray = linksDataArray;
            this.myDiagram.model.commitTransaction('links');
        }
    }

    public addAfterPinsNodes() {
        if (!this.myDiagram) {
            return;
        }

        const inCaps = this.myDiagram.model.nodeDataArray.filter(node => node.group === 'col-1');
        const outCaps = this.myDiagram.model.nodeDataArray.filter(node => node.group === 'col-11');

        const inPins = this.myDiagram.model.nodeDataArray.filter(node => node.group === 'col-in-pins');
        const outPins = this.myDiagram.model.nodeDataArray.filter(node => node.group === 'col-out-pins');

        // const inConnectorPins = this.myDiagram.model.nodeDataArray.filter(node => node.group === 'col-5');
        // const outConnectorPins = this.myDiagram.model.nodeDataArray.filter(node => node.group === 'col-7');

        const allAfterNodes = this.myDiagram.model.nodeDataArray.filter(node => node.category === 'afterPinAddButtonTemplate');
        for (const node of allAfterNodes) {
            this.myDiagram.model.removeNodeData(node);
        }

        setTimeout(() => {
            if (!this.myDiagram) return;
            // Handle IN_CAPS
            // if (inCaps.length > 0) {
            //     this.myDiagram.model.addNodeData({
            //         key: `afterCap-${inCaps[0].key}`,
            //         group: 'col-1',
            //         category: 'afterPinAddButtonTemplate',
            //         sortOrder: 0.5,
            //         type: 'afterCap',
            //         where: 'before',
            //         capId: inCaps[0].key,
            //         direction: CAP_DIRECTION.IN,
            //     });
            //     for (const cap of inCaps) {
            //         this.myDiagram.model.addNodeData({
            //             key: `afterCap-${cap.key}`,
            //             group: cap.group,
            //             category: 'afterPinAddButtonTemplate',
            //             sortOrder: cap.sortOrder ? cap.sortOrder + 0.5 : 0,
            //             type: 'afterCap',
            //             where: 'after',
            //             capId: cap.key,
            //             direction: CAP_DIRECTION.IN,
            //         });
            //     }
            // } else {
            //     this.myDiagram.model.addNodeData({
            //         key: `afterCap-${0}`,
            //         group: 'col-1',
            //         category: 'afterPinAddButtonTemplate',
            //         sortOrder: 0.5,
            //         type: 'afterCap',
            //         where: 'before',
            //         capId: null,
            //         direction: CAP_DIRECTION.IN,
            //     });
            // }

            // // Handle OUT_CAPS
            // if (outCaps.length > 0) {
            //     this.myDiagram.model.addNodeData({
            //         key: `afterCap-${outCaps[0].key}`,
            //         group: 'col-11',
            //         category: 'afterPinAddButtonTemplate',
            //         sortOrder: 0.5,
            //         type: 'afterCap',
            //         where: 'before',
            //         capId: outCaps[0].key,
            //         direction: CAP_DIRECTION.OUT,
            //     });
            //     for (const cap of outCaps) {
            //         this.myDiagram.model.addNodeData({
            //             key: `afterCap-${cap.key}`,
            //             group: cap.group,
            //             category: 'afterPinAddButtonTemplate',
            //             sortOrder: cap.sortOrder ? cap.sortOrder + 0.5 : 0,
            //             type: 'afterCap',
            //             where: 'after',
            //             capId: cap.key,
            //             direction: CAP_DIRECTION.OUT,
            //         });
            //     }
            // } else {
            //     this.myDiagram.model.addNodeData({
            //         key: `afterCap-${0}`,
            //         group: 'col-11',
            //         category: 'afterPinAddButtonTemplate',
            //         sortOrder: 0.5,
            //         type: 'afterCap',
            //         where: 'before',
            //         capId: null,
            //         direction: CAP_DIRECTION.OUT,
            //     });
            // }

            // Handle IN_PINS
            if (inPins.length > 0) {
                this.myDiagram.model.addNodeData({
                    key: `afterPin-${inPins[0].key}`,
                    group: 'col-in-pins',
                    category: 'afterPinAddButtonTemplate',
                    sortOrder: 0.5,
                    type: 'afterPin',
                    where: 'before',
                    pinId: inPins[0].key,
                    direction: PIN_DIRECTION.IN,
                });
                for (const pin of inPins) {
                    this.myDiagram.model.addNodeData({
                        key: `afterPin-${pin.key}`,
                        group: pin.group,
                        category: 'afterPinAddButtonTemplate',
                        sortOrder: pin.sortOrder ? pin.sortOrder + 0.5 : 0,
                        type: 'afterPin',
                        where: 'after',
                        pinId: pin.key,
                        direction: PIN_DIRECTION.IN,
                    });
                }
            } else {
                this.myDiagram.model.addNodeData({
                    key: `afterPin-${0}`,
                    group: 'col-in-pins',
                    category: 'afterPinAddButtonTemplate',
                    sortOrder: 0.5,
                    type: 'afterPin',
                    where: 'before',
                    pinId: null,
                    direction: PIN_DIRECTION.IN,
                });
            }

            // Handle OUT_PINS
            if (outPins.length > 0) {
                this.myDiagram.model.addNodeData({
                    key: `afterPin-${outPins[0].key}`,
                    group: 'col-out-pins',
                    category: 'afterPinAddButtonTemplate',
                    sortOrder: 0.5,
                    type: 'afterPin',
                    where: 'before',
                    pinId: outPins[0].key,
                    direction: PIN_DIRECTION.OUT,
                });
                for (const pin of outPins) {
                    this.myDiagram.model.addNodeData({
                        key: `afterPin-${pin.key}`,
                        group: pin.group,
                        category: 'afterPinAddButtonTemplate',
                        sortOrder: pin.sortOrder ? pin.sortOrder + 0.5 : 0,
                        type: 'afterPin',
                        where: 'after',
                        pinId: pin.key,
                        direction: PIN_DIRECTION.OUT,
                    });
                }
            } else {
                this.myDiagram.model.addNodeData({
                    key: `afterPin-${0}`,
                    group: 'col-out-pins',
                    category: 'afterPinAddButtonTemplate',
                    sortOrder: 0.5,
                    type: 'afterPin',
                    where: 'before',
                    pinId: null,
                    direction: PIN_DIRECTION.OUT,
                });
            }

            // Handle IN_CONNECTOR_PINS
            // if (inConnectorPins.length > 0) {
            //     this.myDiagram.model.addNodeData({
            //         key: `afterPin-${inConnectorPins[0].key}`,
            //         group: 'col-5',
            //         category: 'afterPinAddButtonTemplate',
            //         sortOrder: 0.5,
            //         type: 'afterPin',
            //         where: 'before',
            //         cPinId: inConnectorPins[0].key,
            //     });
            //     for (const pin of inConnectorPins) {
            //         this.myDiagram.model.addNodeData({
            //             key: `afterPin-${pin.key}`,
            //             group: pin.group,
            //             category: 'afterPinAddButtonTemplate',
            //             sortOrder: pin.sortOrder ? pin.sortOrder + 0.5 : 0,
            //             type: 'afterPin',
            //             where: 'after',
            //             cPinId: pin.key,
            //         });
            //     }
            // } else {
            //     this.myDiagram.model.addNodeData({
            //         key: `afterPin-connector-pin-in`,
            //         group: 'col-5',
            //         category: 'afterPinAddButtonTemplate',
            //         sortOrder: 0.5,
            //         type: 'afterPin',
            //         where: 'before',
            //         cPinId: null,
            //     });
            // }

            // Handle OUT_CONNECTOR_PINS
            // if (outConnectorPins.length > 0) {
            //     this.myDiagram.model.addNodeData({
            //         key: `afterPin-${outConnectorPins[0].key}`,
            //         group: 'col-7',
            //         category: 'afterPinAddButtonTemplate',
            //         sortOrder: 0.5,
            //         type: 'afterPin',
            //         where: 'before',
            //         cPinId: outConnectorPins[0].key,
            //     });
            //     for (const pin of outConnectorPins) {
            //         this.myDiagram.model.addNodeData({
            //             key: `afterPin-${pin.key}`,
            //             group: pin.group,
            //             category: 'afterPinAddButtonTemplate',
            //             sortOrder: pin.sortOrder ? pin.sortOrder + 0.5 : 0,
            //             type: 'afterPin',
            //             where: 'after',
            //             cPinId: pin.key,
            //         });
            //     }
            // } else {
            //     this.myDiagram.model.addNodeData({
            //         key: `afterPin-connector-pin-out`,
            //         group: 'col-7',
            //         category: 'afterPinAddButtonTemplate',
            //         sortOrder: 0.5,
            //         type: 'afterPin',
            //         where: 'before',
            //         cPinId: null,
            //     });
            // }
        }, 100);
    }

    public beforeDestroy(): void {}

    fromElement: HTMLElement | null = null;
    fromType: string = '';
    fromPin: EquipmentPinModel | null = null;

    public isStartExists(start: HTMLElement) {
        return false;
    }

    public async onPinCapConnectoin(pinId: string, capId: string) {
        if (this.readonly) return;
        try {
            const pin = [...this.inPinsList, ...this.outPinsList].find(x => x.id === pinId);
            const cap = [...this.inCapsList, ...this.outCapsList].find(x => x.id === capId);

            if (pin && cap) {
                await this.equipmentPinsApi.updatePin(this.locationId, pin.id, {
                    ...pin,
                    // equipmentCapId: cap.id,
                });

                // pin.equipmentCapId = cap.id;
                this.getEquipmentPins();
            }
        } catch (error) {
            console.log(error);
        }
    }

    public async onPinPinConnection(pinId: string, connectedPinId: string) {
        if (this.readonly) return;
        try {
            const pins = [...this.inPinsList, ...this.outPinsList];
            const pin = pins.find(x => x.id === pinId);
            const connectedPin = pins.find(x => x.id === connectedPinId);

            if (pin && connectedPin) {
                await Promise.all([
                    this.equipmentPinsApi.updatePin(this.locationId, pin.id, {
                        ...pin,
                        connectedPinId: connectedPin.id,
                    }),
                    this.equipmentPinsApi.updatePin(this.locationId, connectedPin.id, {
                        ...connectedPin,
                        connectedPinId: pin.id,
                    }),
                ]);

                // pin.connectedPinId = connectedPin.id;
                this.getEquipmentPins();
            }
        } catch (error) {
            console.log(error);
        }
    }

    public async onCableCapConnection(cableId: string, capId: string, cableSide: 'left' | 'right') {
        if (this.readonly) return;
        try {
            const cable = [...this.inCablesList, ...this.outCablesList].find(x => x.id === cableId);
            const cap = [...this.inCapsList, ...this.outCapsList].find(x => x.id === capId);

            if (cable && cap && cable.locationConnectionId) {
                const updateData = new CreateEditCableViewModel({
                    ...cable,
                });

                // Determine which cap field to update based on cable side and cap direction
                if (cableSide === 'left') {
                    // Left side of cable connects to input cap
                    updateData.equipmentInCapId = cap.id;
                } else {
                    // Right side of cable connects to output cap
                    updateData.equipmentOutCapId = cap.id;
                }

                await this.cablesApi.updateCable(cable.locationConnectionId, cable.id!, updateData);

                this.AlertSrv.show('success', `Cable ${cableSide === 'left' ? 'input' : 'output'} cap connected successfully!`);
                this.getEquipmentCaps();
            }
        } catch (error) {
            console.error('Failed to update cable cap connection:', error);
            this.AlertSrv.show('error', 'Failed to save cable cap connection. Please try again.');
        }
    }

    // public async onPinConnectorPinConnection(pinId: string, cPinId: string) {
    //     if (this.readonly) return;
    //     try {
    //         const pins = [...this.inPinsList, ...this.outPinsList];
    //         const pin = pins.find(x => x.id === pinId);
    //         const cPin = [...this.inConnectorPinsList, ...this.outConnectorPinsList].find(x => x.id === cPinId);

    //         if (pin && cPin) {
    //             const pinIds = cPin.equipmentPinIds || [];
    //             if (!pinIds.includes(pin.id)) {
    //                 pinIds.push(pin.id);
    //             }

    //             await Promise.all([
    //                 this.equipmentConnectorPinsApi.updateConnectorPin(this.locationId, cPin.id, {
    //                     ...cPin,
    //                     equipmentPinIds: pinIds,
    //                 }),
    //             ]);
    //             this.getEquipmentConnectorPins();
    //         }
    //     } catch (error) {
    //         console.log(error);
    //     }
    // }

    // public async onCPinCPinConnection(cPinId: string, connectedCPinId: string) {
    //     if (this.readonly) return;
    //     try {
    //         const cPins = [...this.inConnectorPinsList, ...this.outConnectorPinsList];
    //         const cPin = cPins.find(x => x.id === cPinId);
    //         const cPinConnectedIds = cPin?.equipmentConnectorPinIds || [];

    //         if (!cPinConnectedIds.includes(connectedCPinId)) {
    //             cPinConnectedIds.push(connectedCPinId);
    //         }

    //         const connectedCPin = cPins.find(x => x.id === connectedCPinId);
    //         const connectedPinCIds = connectedCPin?.equipmentConnectorPinIds || [];

    //         if (!connectedPinCIds.includes(cPinId)) {
    //             connectedPinCIds.push(cPinId);
    //         }

    //         if (cPin && connectedCPin) {
    //             await Promise.all([
    //                 this.equipmentConnectorPinsApi.updateConnectorPin(this.locationId, cPinId, {
    //                     ...cPin,
    //                     equipmentConnectorPinIds: cPinConnectedIds,
    //                 }),
    //                 this.equipmentConnectorPinsApi.updateConnectorPin(this.locationId, connectedCPin.id, {
    //                     ...connectedCPin,
    //                     equipmentConnectorPinIds: connectedPinCIds,
    //                 }),
    //             ]);
    //             this.getEquipmentConnectorPins();
    //         }
    //     } catch (error) {
    //         console.log(error);
    //     }
    // }

    private getRandomDarkViewableColor() {
        const getRandomValue = () => Math.floor(Math.random() * 129) + 64;
        const red = getRandomValue().toString(16).padStart(2, '0');
        const green = getRandomValue().toString(16).padStart(2, '0');
        const blue = getRandomValue().toString(16).padStart(2, '0');
        return `#${red}${green}${blue}`;
    }

    private createAndSaveLink(pin: EquipmentPinModel, fromElement: HTMLElement, toElement: HTMLElement, isPinConnection: boolean = false) {}

    private removeAllLinks() {}

    private removeLink(pinId: string) {}

    public getEquipmentPins() {
        this.LoaderSrv.showFullScreenLoader('Loading pins details');
        return this.equipmentPinsApi
            .getLocationPins(this.locationId)
            .then(res => {
                this.inPinsList = res?.filter(x => x.pinDirection === PIN_DIRECTION.IN) ?? [];
                this.outPinsList = res?.filter(x => x.pinDirection === PIN_DIRECTION.OUT) ?? [];
                console.log('diagram1', this.myDiagram);
                this.updateNodeDataArray(); // Update nodeDataArray and refresh diagram
                return res;
            })
            .catch(e => {
                console.log(e);
            })
            .finally(() => {
                this.LoaderSrv.hideFullScreenLoader();
            });
    }

    // public getEquipmentConnectorPins() {
    //     this.LoaderSrv.showFullScreenLoader('Loading Connector Pins...');
    //     return this.equipmentConnectorPinsApi
    //         .getLocationConnectorPins(this.locationId)
    //         .then(res => {
    //             this.inConnectorPinsList = res?.filter(x => x.pinDirection === PIN_DIRECTION.IN) ?? [];
    //             this.outConnectorPinsList = res.filter(x => x.pinDirection === PIN_DIRECTION.OUT) ?? [];

    //             this.updateNodeDataArray();

    //             return res;
    //         })
    //         .catch(e => {
    //             console.log(e);
    //         })
    //         .finally(() => {
    //             this.LoaderSrv.hideFullScreenLoader();
    //         });
    // }

    public getEquipmentCaps() {
        this.LoaderSrv.showFullScreenLoader('Loading caps details');
        return this.equipmentCapsApi
            .getLocationCaps(this.locationId, {
                include: [
                    {
                        relation: 'inCables',
                        scope: {
                            include: [
                                {
                                    relation: 'cableCores',
                                },
                            ],
                        },
                    },
                    {
                        relation: 'outCables',
                        scope: {
                            include: [
                                {
                                    relation: 'cableCores',
                                },
                            ],
                        },
                    },
                    {
                        relation: 'inCores',
                    },
                    {
                        relation: 'outCores',
                    },
                ],
            })
            .then(res => {
                this.inCapsList = res?.filter(x => x.capDirection === CAP_DIRECTION.IN) ?? [];
                this.outCapsList = res?.filter(x => x.capDirection === CAP_DIRECTION.OUT) ?? [];

                this.inCablesList = this.inCapsList.flatMap(c => c.inCables ?? []);
                this.outCablesList = this.outCapsList.flatMap(c => c.outCables ?? []);

                this.updateNodeDataArray(); // Update nodeDataArray and refresh diagram
                return res;
            })
            .catch(e => {
                console.log(e);
            })
            .finally(() => {
                this.LoaderSrv.hideFullScreenLoader();
            });
    }

    public getInPinIdsFromCap(capId: string) {
        const cap = this.inCapsList.find(c => c.id === capId);
        let pinIds: string[] = [];
        if (cap) {
            const pinsFromCable = cap.inCables?.flatMap(cable => cable.cableCores?.map(core => core.equipmentInPinId)) || [];
            const pinsFromCore = cap.inCores?.map(core => core.equipmentOutPinId) || [];

            pinIds = [...pinsFromCable.filter(id => id != null), ...pinsFromCore.filter(id => id != null)] as string[];
        }

        return pinIds;
    }

    public getOutPinIdsFromCap(capId: string) {
        const cap = this.outCapsList.find(c => c.id === capId);
        let pinIds: string[] = [];
        if (cap) {
            const pinsFromCable = cap.outCables?.flatMap(cable => cable.cableCores?.map(core => core.equipmentOutPinId)) || [];
            const pinsFromCore = cap.outCores?.map(core => core.equipmentOutPinId) || [];

            pinIds = [...pinsFromCable.filter(id => id != null), ...pinsFromCore.filter(id => id != null)] as string[];
        }

        return pinIds;
    }

    // public addPins(direction: PIN_DIRECTION) {
    //     if ([PIN_DIRECTION.IN, PIN_DIRECTION.OUT].includes(direction)) {
    //         const count = direction === PIN_DIRECTION.IN ? this.pinsInToAddCount : this.pinsOutToAddCount;
    //         const pins = Array.from({length: count}, () => {
    //             const random = Math.floor(Math.random() * 16777215).toString(16);
    //             return new CreateEditEquipmentPinViewModel({
    //                 title: `Pin ${random}`,
    //                 pinDirection: direction,
    //                 sortOrder: 0,
    //             });
    //         });

    //         let maxOrder =
    //             direction === PIN_DIRECTION.IN
    //                 ? Math.min(...this.inPinsList.map(x => x.sortOrder ?? 0))
    //                 : Math.max(...this.outPinsList.map(x => x.sortOrder ?? 0));

    //         if (maxOrder === Infinity || maxOrder === -Infinity) {
    //             maxOrder = 0;
    //         }

    //         this.LoaderSrv.showFullScreenLoader();
    //         this.equipmentPinsApi
    //             .createPinsMultiple(this.locationId, maxOrder, pins)
    //             .then(() => {
    //                 this.getEquipmentPins().finally(() => {
    //                     // this.rerenderLinks();
    //                 });
    //                 this.AlertSrv.show('success', 'Pins added successfully!');
    //             })
    //             .catch(e => {
    //                 this.AlertSrv.show('error', 'Unable to add pins please try again later!');
    //             })
    //             .finally(() => {
    //                 this.pinsInToAddCount = this.pinsOutToAddCount = 0;
    //                 this.LoaderSrv.hideFullScreenLoader();
    //             });
    //     } else {
    //         this.AlertSrv.show('error', 'Invalid pin direction!');
    //     }
    // }

    // public addCaps(direction: CAP_DIRECTION) {
    //     if ([CAP_DIRECTION.IN, CAP_DIRECTION.OUT].includes(direction)) {
    //         const count = direction === CAP_DIRECTION.IN ? this.capsInToAddCount : this.capsOutToAddCount;
    //         const caps = Array.from({length: count}, () => {
    //             const random = Math.floor(Math.random() * 16777215).toString(16);
    //             return new CreateEditEquipmentCapViewModel({
    //                 title: `Cap ${random}`,
    //                 capDirection: direction,
    //                 sortOrder: 0,
    //             });
    //         });

    //         let maxOrder =
    //             direction === CAP_DIRECTION.IN
    //                 ? Math.min(...this.inCapsList.map(x => x.sortOrder ?? 0))
    //                 : Math.max(...this.outCapsList.map(x => x.sortOrder ?? 0));

    //         if (maxOrder === Infinity || maxOrder === -Infinity) {
    //             maxOrder = 0;
    //         }

    //         this.LoaderSrv.showFullScreenLoader();
    //         this.equipmentCapsApi
    //             .createCapsMultiple(this.locationId, maxOrder, caps)
    //             .then(() => {
    //                 this.getEquipmentCaps().finally(() => {
    //                     // this.rerenderLinks();
    //                 });
    //                 this.AlertSrv.show('success', 'Caps added successfully!');
    //             })
    //             .catch(e => {
    //                 this.AlertSrv.show('error', 'Unable to add caps please try again later!');
    //             })
    //             .finally(() => {
    //                 this.capsInToAddCount = this.capsOutToAddCount = 0;
    //                 this.LoaderSrv.hideFullScreenLoader();
    //             });
    //     } else {
    //         this.AlertSrv.show('error', 'Invalid cap direction!');
    //     }
    // }

    addSingleCap(cap: EquipmentCapModel, where: 'before' | 'after') {
        const random = Math.floor(Math.random() * 16777215).toString(16);
        const newCap = new CreateEditEquipmentCapViewModel({
            title: `Cap ${random}`,
            capDirection: cap.capDirection,
            sortOrder: 0,
        });

        const afterOrder = where === 'before' ? 0 : cap.sortOrder;

        this.LoaderSrv.showFullScreenLoader('Adding cap...');
        this.equipmentCapsApi
            .createCapsMultiple(this.locationId, afterOrder, [newCap])
            .then(() => {
                this.getEquipmentCaps().finally(() => {
                    // this.rerenderLinks();
                });
                this.AlertSrv.show('success', 'Cap added successfully!');
            })
            .catch(e => {
                this.AlertSrv.show('error', 'Unable to add cap please try again later!');
            })
            .finally(() => {
                this.LoaderSrv.hideFullScreenLoader();
            });
    }

    addSinglePin(pin: EquipmentPinModel, where: 'before' | 'after') {
        const random = Math.floor(Math.random() * 16777215).toString(16);
        const newPin = new CreateEditEquipmentPinViewModel({
            title: `Pin ${random}`,
            pinDirection: pin.pinDirection,
            sortOrder: 0,
        });

        const afterOrder = where === 'before' ? 0 : pin.sortOrder;

        this.LoaderSrv.showFullScreenLoader('Adding pin...');
        this.equipmentPinsApi
            .createPinsMultiple(this.locationId, afterOrder, [newPin])
            .then(() => {
                this.getEquipmentPins().finally(() => {
                    // this.rerenderLinks();
                });
                this.AlertSrv.show('success', 'Pin added successfully!');
            })
            .catch(e => {
                this.AlertSrv.show('error', 'Unable to add pin please try again later!');
            })
            .finally(() => {
                this.LoaderSrv.hideFullScreenLoader();
            });
    }

    // addSingleConnectorPin(pin: EquipmentConnectorPinModel, where: 'before' | 'after') {
    //     const random = Math.floor(Math.random() * 16777215).toString(16);
    //     const newPin = new CreateEditEquipmentConnectorPinViewModel({
    //         title: `CPin ${random}`,
    //         pinDirection: pin.pinDirection,
    //         sortOrder: 0,
    //     });

    //     const afterOrder = where === 'before' ? 0 : pin.sortOrder;

    //     this.LoaderSrv.showFullScreenLoader('Adding pin...');
    //     this.equipmentConnectorPinsApi
    //         .createConnectorPinsMultiple(this.locationId, afterOrder, [newPin])
    //         .then(() => {
    //             this.getEquipmentConnectorPins().finally(() => {
    //                 // this.rerenderLinks();
    //             });
    //             this.AlertSrv.show('success', 'Connector Pin added successfully!');
    //         })
    //         .catch(e => {
    //             this.AlertSrv.show('error', 'Unable to add pin please try again later!');
    //         })
    //         .finally(() => {
    //             this.LoaderSrv.hideFullScreenLoader();
    //         });
    // }

    onPinRadioClick(pin: EquipmentPinModel) {
        if (pin.isActive) {
            this.ConfirmSrv.open('Deactivate Pin?', 'Are you sure you want to deactivate this pin?').then(y => {
                if (y) {
                    this.LoaderSrv.showFullScreenLoader('Deactivating pin...');
                    this.equipmentPinsApi
                        .markPinInactive(pin.id)
                        .then(() => {
                            this.getEquipmentPins().finally(() => {
                                // this.rerenderLinks();
                            });
                            this.AlertSrv.show('success', 'Pin has been deactivated.');
                        })
                        .catch(e => {
                            this.AlertSrv.show('error', 'Unable to deactivate pin.');
                        })
                        .finally(() => {
                            this.LoaderSrv.hideFullScreenLoader();
                        });
                }
            });
        } else {
            this.ConfirmSrv.open('Activate Pin?', 'Are you sure you want to activate this pin?').then(y => {
                if (y) {
                    this.LoaderSrv.showFullScreenLoader('Activating pin...');
                    this.equipmentPinsApi
                        .markPinActive(pin.id)
                        .then(() => {
                            this.getEquipmentPins().finally(() => {
                                // this.rerenderLinks();
                            });
                            this.AlertSrv.show('success', 'Pin has been activated.');
                        })
                        .catch(e => {
                            this.AlertSrv.show('error', 'Unable to activate pin.');
                        })
                        .finally(() => {
                            this.LoaderSrv.hideFullScreenLoader();
                        });
                }
            });
        }
    }

    onAddPinClick() {
        this.CoreSrv.OpenModal(this.name);
    }

    onDeletePinClick(pin: EquipmentPinModel) {
        if (!this.hasCircuitEditPermission || this.readonly) return;

        this.ConfirmSrv.open('Delete Pin?', 'Are you sure you want to delete this pin?').then(y => {
            if (y) {
                this.LoaderSrv.showFullScreenLoader('Deleting pin...');
                this.equipmentPinsApi
                    .deletePin(pin.id)
                    .then(() => {
                        this.getEquipmentPins().finally(() => {
                            // this.rerenderLinks();
                        });
                        this.AlertSrv.show('success', 'Pin has been delete.');
                    })
                    .catch(e => {
                        this.AlertSrv.show('error', 'Unable to delete pin.');
                    })
                    .finally(() => {
                        this.LoaderSrv.hideFullScreenLoader();
                    });
            }
        });
    }

    onDeleteCapClick(cap: EquipmentCapModel) {
        if (!this.hasCircuitEditPermission || this.readonly) return;

        this.ConfirmSrv.open('Delete Cap?', 'Are you sure you want to delete this cap?').then(y => {
            if (y) {
                this.LoaderSrv.showFullScreenLoader('Deleting cap...');
                this.equipmentCapsApi
                    .deleteCap(cap.id)
                    .then(() => {
                        this.getEquipmentCaps().finally(() => {
                            // this.rerenderLinks();
                        });
                        this.AlertSrv.show('success', 'Cap has been delete.');
                    })
                    .catch(e => {
                        this.AlertSrv.show('error', 'Unable to delete cap.');
                    })
                    .finally(() => {
                        this.LoaderSrv.hideFullScreenLoader();
                    });
            }
        });
    }

    // onDeleteConnectorPinClick(pin: EquipmentConnectorPinModel) {
    //     if (this.readonly) return;

    //     this.ConfirmSrv.open('Delete Connector Pin?', 'Are you sure you want to delete this connector pin?').then(y => {
    //         if (y) {
    //             this.LoaderSrv.showFullScreenLoader('Deleting connector pin...');
    //             this.equipmentConnectorPinsApi
    //                 .deleteConnectorPin(pin.id)
    //                 .then(() => {
    //                     this.getEquipmentConnectorPins().finally(() => {
    //                         // this.rerenderLinks();
    //                     });
    //                     this.AlertSrv.show('success', 'Connector Pin has been delete.');
    //                 })
    //                 .catch(e => {
    //                     this.AlertSrv.show('error', 'Unable to delete connector pin.');
    //                 })
    //                 .finally(() => {
    //                     this.LoaderSrv.hideFullScreenLoader();
    //                 });
    //         }
    //     });
    // }

    public onCapCreated(cap: EquipmentCapModel): void {
        // Refresh the caps list and clear selection
        this.getEquipmentCaps();
        this.selectedCap = null;
    }

    public onCapUpdated(cap: EquipmentCapModel): void {
        // Refresh the caps list and clear selection
        this.getEquipmentCaps();
        this.selectedCap = null;
    }

    public onFormReset(): void {
        // Clear the selected cap when form is reset
        this.selectedCap = null;
    }

    public onCapClick(cap: EquipmentCapModel): void {
        // Set the selected cap for editing
        this.selectedCap = cap;
        // Clear pin selection when cap is selected
        this.selectedPin = null;
    }

    public onPinClick(pin: EquipmentPinModel): void {
        // Set the selected pin for editing
        this.selectedPin = pin;
        // Clear cap selection when pin is selected
        this.selectedCap = null;
    }

    // Pin form event handlers
    public onPinCreated(newPin: EquipmentPinModel): void {
        console.log('Pin created:', newPin);
        // Refresh the pins list
        this.getEquipmentPins();
        // Optionally select the new pin
        this.selectedPin = newPin;
    }

    public onPinUpdated(pinId: string): void {
        console.log('Pin updated:', pinId);
        // Refresh the pins list and clear selection
        this.getEquipmentPins();
        this.selectedPin = null;
    }

    public onPinFormReset(): void {
        // Clear the selected pin when form is reset
        this.selectedPin = null;
    }

    close() {
        this.CoreSrv.CloseModal(this.name);
    }
}
